/**
 * VehicleInfoTabs — replaces the long scrolling accordion with a
 * sticky tab bar at the top, so users can jump straight to the
 * info group they care about (Key Info, Programming, Pricing, etc.)
 * without scrolling past everything.
 *
 * Re-uses the existing `renderSection(id, vehicle)` rendering logic
 * from VehicleInfoAccordion — no duplicate field markup.
 */
import React, { useState } from 'react';
import {
  Key,
  Cpu,
  Wrench,
  Zap,
  AlertCircle,
  Lightbulb,
  DollarSign,
  FileText,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { renderSection } from './VehicleInfoAccordion';

// Group the 17 accordion sections into 8 navigation tabs (matches original).
const TABS = [
  {
    id: 'key-info',
    label: 'Key Info',
    icon: Key,
    color: 'text-cyan-400',
    activeBg: 'bg-cyan-500/10 border-cyan-500/40',
    sections: [
      { id: 'key-blank', title: 'Key Blank Details' },
      { id: 'key-info', title: 'Key Information Summary' },
    ],
  },
  {
    id: 'programming',
    label: 'Programming',
    icon: Cpu,
    color: 'text-purple-400',
    activeBg: 'bg-purple-500/10 border-purple-500/40',
    sections: [
      { id: 'programming', title: 'Programming' },
      { id: 'ecu-bcm', title: 'ECU / BCM Programming' },
    ],
  },
  {
    id: 'equipment',
    label: 'Equipment',
    icon: Wrench,
    color: 'text-orange-400',
    activeBg: 'bg-orange-500/10 border-orange-500/40',
    sections: [
      { id: 'equipment', title: 'Compatible Equipment' },
      { id: 'transponder', title: 'Transponder & Chip' },
      { id: 'smart-key', title: 'Smart Key System' },
    ],
  },
  {
    id: 'ignition',
    label: 'Ignition & Locks',
    icon: Zap,
    color: 'text-amber-400',
    activeBg: 'bg-amber-500/10 border-amber-500/40',
    sections: [
      { id: 'ignition', title: 'Ignition Info & Pricing' },
      { id: 'door-locks', title: 'Door Locks & Pricing' },
    ],
  },
  {
    id: 'diagnostics',
    label: 'Diagnostics',
    icon: AlertCircle,
    color: 'text-red-400',
    activeBg: 'bg-red-500/10 border-red-500/40',
    sections: [
      { id: 'diagnostics', title: 'Diagnostic Procedures' },
      { id: 'troubleshooting', title: 'Troubleshooting' },
      { id: 'warnings', title: 'Warnings & Issues' },
    ],
  },
  {
    id: 'pricing',
    label: 'Pricing',
    icon: DollarSign,
    color: 'text-green-400',
    activeBg: 'bg-green-500/10 border-green-500/40',
    sections: [
      { id: 'pricing', title: 'Pricing & Profit' },
      { id: 'cost-breakdown', title: 'Cost Breakdown' },
    ],
  },
  {
    id: 'tips',
    label: 'Field Tips',
    icon: Lightbulb,
    color: 'text-yellow-400',
    activeBg: 'bg-yellow-500/10 border-yellow-500/40',
    sections: [
      { id: 'field-tips', title: 'Field Tips & Best Practices' },
      { id: 'service-bulletins', title: 'Service Bulletins' },
    ],
  },
  {
    id: 'supplier',
    label: 'Supplier',
    icon: FileText,
    color: 'text-sky-400',
    activeBg: 'bg-sky-500/10 border-sky-500/40',
    sections: [
      { id: 'supplier', title: 'Supplier & Sourcing' },
    ],
  },
];

export default function VehicleInfoTabs({ vehicle }) {
  const [activeTab, setActiveTab] = useState('key-info');
  if (!vehicle) return null;

  const active = TABS.find((t) => t.id === activeTab) || TABS[0];

  return (
    <div className="space-y-4 fade-in" data-testid="vehicle-info-tabs">
      {/* Sticky tab bar — stays visible while scrolling so the user
          can switch sections without scrolling back up */}
      <div
        className="sticky top-0 z-20 -mx-1 px-1 py-2 bg-background/95 backdrop-blur-md border-b border-border/40"
        data-testid="vehicle-tabs-bar"
      >
        <div className="grid grid-cols-4 md:grid-cols-8 gap-1.5">
          {TABS.map(({ id, label, icon: Icon, color, activeBg }) => {
            const isActive = id === activeTab;
            return (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                data-testid={`vehicle-tab-${id}`}
                className={cn(
                  'flex flex-col items-center justify-center gap-1 px-1.5 py-2 rounded-lg border transition-all duration-150 text-xs md:text-sm font-medium active:scale-95',
                  isActive
                    ? `${activeBg} ${color}`
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/40'
                )}
              >
                <Icon className="w-4 h-4 md:w-5 md:h-5" />
                <span className="leading-tight text-center">{label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Active tab content — renders all sections in this group, stacked */}
      <div className="space-y-4" data-testid={`vehicle-tab-content-${active.id}`}>
        {active.sections.map((s) => (
          <div
            key={s.id}
            className="rounded-2xl border border-border/60 bg-card/60 p-4 md:p-5 shadow-sm"
            data-testid={`tab-section-${s.id}`}
          >
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border/40">
              <active.icon className={cn('w-4 h-4', active.color)} />
              <h3 className="font-semibold text-base">{s.title}</h3>
            </div>
            {renderSection(s.id, vehicle)}
          </div>
        ))}
      </div>
    </div>
  );
}
